// MACHINERY V2 (Stroje) — the three field rows the custom-machine form repeats
// (M2.7). Split out of CustomMachineFields.tsx for the 300-line cap.
//
// Each row is FormField (teal glyph + mono eyebrow + error slot) wrapped around
// one control from the M2.6 kit. Nothing new is invented: the text row is the
// kit's `.mv2-input` class, the select row is forms/FormSelect (Radix), and the
// free-solo row is `.mv2-input` plus a native <datalist>.
//
// WHY <datalist> FOR THE FREE-SOLO ROW. `system` is v1's ONLY freeSolo control
// (CncForm.tsx:235-259): the user may TYPE a control-system model that is not
// in the list, or pick one that is. FormSelect cannot express that — it returns
// an option id and has no text entry. A native datalist keeps both halves with
// no new primitive and no MUI, and it is the same class of call M2.6 already
// made and recorded for native date/time inputs ("ARTBOARD-FAITHFUL … so nobody
// upgrades them to a custom picker later"). Artboard 0803 draws this field as a
// plain input with no chevron, which is exactly what a datalist input renders.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";
import { Controller, FieldPath, RegisterOptions, UseFormReturn } from "react-hook-form";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { ChangeMachine } from "../../models/models";
import FormField from "../forms/FormField";
import FormSelect, { FormSelectOption } from "../forms/FormSelect";

export type MachineField = FieldPath<ChangeMachine>;

export const TwoCol: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, minWidth: 0 }}>
    {children}
  </div>
);

/**
 * The RHF error ENTRY, not its text.
 *
 * WHY BOTH EXIST. A rule registered as `{ required: true }` still produces an
 * entry, but its message is the EMPTY STRING: react-hook-form 7.51.2 normalises
 * a bare rule value with `e => i(e) && !ee(e) ? e : { value: e, message: "" }`
 * and builds the error as `{ type, message: Y(e) ? e : "", ref }` (verified in
 * node_modules/react-hook-form/dist/index.cjs.js, minified exactly so). An
 * empty message renders nothing (FormField.tsx:57 `if (!message) return null`)
 * and `!!""` is false, so anything that drives a red border off the TEXT stays
 * dark while submit is silently blocked. `invalid` therefore keys off the
 * entry's existence; the visible message keys off its text.
 */
const fieldError = (
  form: UseFormReturn<ChangeMachine>,
  name: MachineField
): { message?: string } | undefined =>
  form.formState.errors[name as keyof ChangeMachine] as { message?: string } | undefined;

const errorText = (form: UseFormReturn<ChangeMachine>, name: MachineField): string | undefined =>
  fieldError(form, name)?.message;

export interface TextRowProps {
  form: UseFormReturn<ChangeMachine>;
  name: MachineField;
  label: string;
  glyph?: React.ReactNode;
  required?: boolean;
  placeholder?: string;
  maxLength?: number;
  /** JetBrains Mono — the artboards set it on serial / year / station counts. */
  mono?: boolean;
  rules?: RegisterOptions<ChangeMachine, MachineField>;
}

export const TextRow: React.FC<TextRowProps> = ({
  form,
  name,
  label,
  glyph,
  required,
  placeholder,
  maxLength,
  mono,
  rules,
}) => {
  const message = errorText(form, name);
  return (
    <FormField glyph={glyph} label={label} required={required} error={message} htmlFor={name}>
      <input
        id={name}
        type="text"
        className={"mv2-input" + (message ? " is-invalid" : "")}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? name + "-error" : undefined}
        style={mono ? { fontFamily: "var(--font-mono)" } : undefined}
        {...form.register(name, rules)}
      />
    </FormField>
  );
};

export interface SelectRowProps {
  form: UseFormReturn<ChangeMachine>;
  name: MachineField;
  label: string;
  glyph?: React.ReactNode;
  required?: boolean;
  placeholder: string;
  options: FormSelectOption[];
  /**
   * §10's "trigger on close" quirk: v1's MUI Selects re-validate right after
   * the menu closes (`onClose={() => setTimeout(() => trigger(name))}`,
   * CncForm.tsx:102). A Radix menu only reports a CHOICE, so the equivalent is
   * to re-validate right after one — same moment the user sees the value land.
   */
  onPicked?: () => void;
}

export const SelectRow: React.FC<SelectRowProps> = ({
  form,
  name,
  label,
  glyph,
  required,
  placeholder,
  options,
  onPicked,
}) => {
  const { translate } = useTranslation(["common"]);
  const entry = fieldError(form, name);
  const message = entry?.message;
  return (
    <FormField glyph={glyph} label={label} required={required} error={message} htmlFor={name}>
      <Controller
        name={name}
        control={form.control}
        // The message is MANDATORY, not decoration — see fieldError above. v1
        // passes the same translated string to every SELECT it registers
        // (machineTypeForms/CncForm.tsx:103, :197). Where v1 does register a
        // bare `{ required: true }` — the axis Autocomplete, CncForm.tsx:152 —
        // it pays for it twice over: `error={!!errors.axisConfigurationId}`
        // (:153) for the border and a hand-written
        // `helperText={... ? tCommon("forms.required") : ""}` (:154) for the
        // text. One translated message plus an entry-driven `invalid` buys
        // both here.
        rules={required ? { required: translate("common:forms.required") } : undefined}
        render={({ field }) => (
          <FormSelect
            id={name}
            ariaLabel={label}
            placeholder={placeholder}
            value={typeof field.value === "string" ? field.value : undefined}
            options={options}
            invalid={!!entry}
            describedBy={message ? name + "-error" : undefined}
            onChange={(v) => {
              field.onChange(v ?? "");
              if (onPicked) onPicked();
              else form.trigger(name);
            }}
          />
        )}
      />
    </FormField>
  );
};

export interface FreeSoloRowProps extends Omit<TextRowProps, "mono"> {
  /** Suggestion strings — a datalist, so an unlisted value is still valid. */
  suggestions: string[];
  disabled?: boolean;
}

export const FreeSoloRow: React.FC<FreeSoloRowProps> = ({
  form,
  name,
  label,
  glyph,
  required,
  placeholder,
  maxLength,
  rules,
  suggestions,
  disabled,
}) => {
  const message = errorText(form, name);
  const listId = name + "-list";
  return (
    <FormField glyph={glyph} label={label} required={required} error={message} htmlFor={name}>
      <input
        id={name}
        type="text"
        list={listId}
        disabled={disabled}
        className={"mv2-input" + (message ? " is-invalid" : "")}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-invalid={message ? true : undefined}
        aria-describedby={message ? name + "-error" : undefined}
        {...form.register(name, rules)}
      />
      <datalist id={listId}>
        {suggestions.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </FormField>
  );
};
